import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';

/**
 * Drawing a PDF ourselves.
 *
 * The first version of the documents page put the PDF in an iframe and let
 * the browser's own viewer draw it. That is the obvious thing to do and it did
 * not work: an embedded PDF is at the mercy of the browser's plugin policy and
 * the page's own content-security rules, and what Trevor got was a grey box
 * with a broken-file icon in it.
 *
 * Every site that shows PDFs reliably — Drive, Dropbox, every e-signature
 * service — renders them itself instead, and so does this now. pdf.js reads
 * the file and paints each page onto a canvas. It costs a download, and buys
 * a preview that behaves the same in every browser, a viewer we control, and
 * printing that does not depend on a plugin honouring a print request.
 */

/*
 * The worker, resolved through the bundler.
 *
 * `new URL(..., import.meta.url)` is what tells Vite to emit the worker as an
 * asset and hand back its hashed path — so it is served from this origin like
 * everything else, and `worker-src 'self'` is enough. A CDN would have meant
 * loosening the content-security policy to fetch a script from someone else's
 * server, on a site that holds a class list.
 */
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

/**
 * Documents already opened, so paging through a viewer or re-rendering a
 * thumbnail does not fetch and parse the same file again.
 */
const opened = new Map<string, Promise<PDFDocumentProxy>>();

export function loadPdf(url: string): Promise<PDFDocumentProxy> {
  const existing = opened.get(url);
  if (existing) return existing;

  const task = pdfjs.getDocument({ url, withCredentials: true }).promise;
  opened.set(url, task);

  // A failure must not be cached forever — she may simply have been offline.
  void task.catch(() => opened.delete(url));

  return task;
}

export interface RenderedPage {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

/**
 * One page, drawn at whatever width it is being shown at.
 *
 * `scale` is worked out from the page's own size rather than fixed, so a
 * legal-size form and a postcard both come out the width asked for. The
 * device pixel ratio is folded in, or the text is soft on a good screen and
 * unreadable in a thumbnail.
 */
export async function renderPage(
  pdf: PDFDocumentProxy,
  pageNumber: number,
  targetWidth: number,
): Promise<RenderedPage> {
  const page = await pdf.getPage(pageNumber);

  const base = page.getViewport({ scale: 1 });
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  const viewport = page.getViewport({ scale: (targetWidth / base.width) * ratio });

  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);

  const context = canvas.getContext('2d');
  if (!context) throw new Error('no 2d context');

  await page.render({ canvasContext: context, viewport, canvas }).promise;

  return {
    canvas,
    width: viewport.width / ratio,
    height: viewport.height / ratio,
  };
}

/** A page as a data URL, for an `<img>` that needs no lifecycle. */
export async function renderPageToImage(
  url: string,
  pageNumber: number,
  targetWidth: number,
): Promise<string> {
  const pdf = await loadPdf(url);
  const { canvas } = await renderPage(pdf, pageNumber, targetWidth);
  return canvas.toDataURL('image/png');
}
